import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { fmtDuration, fmtDate, STATUS_COLOR, PRIORITY_COLOR } from "@/lib/supportApi";
import { ReviewDialog } from "@/components/support/ReviewDialog";
import { Ticket, ClipboardText, Timer, WarningCircle } from "@phosphor-icons/react";

function Stat({ label, value, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-white/10 bg-zinc-900/60 p-4">
      <div className="flex items-center gap-2 text-zinc-400 text-xs uppercase tracking-wider"><Icon size={15} /> {label}</div>
      <div className="mt-2 text-3xl font-heading font-bold">{value}</div>
    </div>
  );
}

export default function SupportDashboard() {
  const { currentOrgId } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [reviewSession, setReviewSession] = useState(null);

  const load = () => { if (currentOrgId) api.get(`/support/dashboard?org_id=${currentOrgId}`).then((r) => setD(r.data)).catch(() => {}); };
  useEffect(load, [currentOrgId]);
  if (!d) return <div className="text-zinc-500 text-sm">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="support-dashboard">
      <h1 className="font-heading text-2xl font-bold tracking-tight">Support</h1>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat testid="stat-open-tickets" label="Open tickets" value={d.tickets_open} icon={Ticket} />
        <Stat testid="stat-needs-review" label="Needs review" value={d.needs_review} icon={ClipboardText} />
        <Stat testid="stat-week-hours" label="Hours (7d)" value={fmtDuration(d.week_actual_seconds)} icon={Timer} />
        <Stat testid="stat-unclassified" label="Unclassified time" value={d.unclassified_time} icon={WarningCircle} />
      </div>

      <div className="rounded-xl border border-white/10 bg-zinc-900/60">
        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
          <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-zinc-300">Needs review</h2>
        </div>
        {d.needs_review_list.length === 0 ? (
          <div className="p-6 text-center text-sm text-zinc-500">No sessions awaiting review 🎉</div>
        ) : (
          <div className="divide-y divide-white/5">
            {d.needs_review_list.map((s) => (
              <div key={s.session_id} data-testid={`needs-review-row-${s.session_id}`} className="flex items-center justify-between px-4 py-3 hover:bg-white/5">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{s.machine_name} · {s.customer_name || "— no customer —"}</div>
                  <div className="text-xs text-zinc-500">{fmtDate(s.ended_at)} · connection {fmtDuration(s.duration_seconds)}</div>
                </div>
                <button data-testid={`review-open-${s.session_id}`} onClick={() => setReviewSession(s)} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-blue-600 hover:bg-blue-500">Review</button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-xl border border-white/10 bg-zinc-900/60">
        <div className="px-4 py-3 border-b border-white/10"><h2 className="font-heading text-sm font-bold uppercase tracking-wider text-zinc-300">Recent tickets</h2></div>
        <div className="divide-y divide-white/5">
          {d.recent_tickets.map((t) => (
            <button key={t.ticket_id} onClick={() => navigate(`/helpdesk/tickets/${t.ticket_id}`)} className="w-full text-left flex items-center justify-between px-4 py-3 hover:bg-white/5">
              <div className="min-w-0"><span className="font-mono text-xs text-zinc-500 mr-2">#{t.ticket_number}</span><span className="text-sm">{t.subject}</span></div>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] px-1.5 py-0.5 rounded border ${PRIORITY_COLOR[t.priority]}`}>{t.priority}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded border ${STATUS_COLOR[t.status]}`}>{t.status}</span>
              </div>
            </button>
          ))}
          {d.recent_tickets.length === 0 && <div className="p-6 text-center text-sm text-zinc-500">No tickets yet</div>}
        </div>
      </div>

      <ReviewDialog open={!!reviewSession} onOpenChange={(o) => !o && setReviewSession(null)} session={reviewSession} onReviewed={load} />
    </div>
  );
}
